import { Component, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {
    AbstractControl,
    FormGroupDirective,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors
} from '@angular/forms';
import { U2bValidators } from '@shared/validators/validators';
import { BoatApiService } from '@modules/bcm/@shared/services';
import { U2bNumericValidators } from '@shared/validators/numeric';
import { BehaviorSubject, combineLatest, Observable, of, Subject, withLatestFrom } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { Boat } from '@shared/models/boat';
import { BoatOption } from '@shared/models/boat-option';
import { disableControlsByName } from '@shared/functions/form/disable-controls';
import { enableControlsByName } from '@shared/functions/form/enable-controls';
import { DEFAULT_DEBOUNCE_TIME } from '@modules/bcm/@shared/constants';
import { isString } from '@shared/functions/is-string';
import { isUp2boatId, Up2boatIdSelectionService } from '@modules/bcm/@shared/services/up2boat-id-selection.service';
import { Berth } from '@shared/models/berth';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { TranslationService } from '@core/translation/translation.service';
import { MatDialog } from '@angular/material/dialog';
import { BoatEditorDialogComponent } from '@bcm-work-flows/@form-widgets/boat/boat-editor-dialog/boat-editor-dialog.component';
import { isFunction } from '@shared/functions/is-function';
import { Company } from '@shared/models/company';
import { Person } from '@shared/models/person';
import { BcmBookingGeneralData } from '@shared/models/bcm-booking';
import { PredefinedBoatValues } from '@sharedComponents/dialogs/booking-dialog/services/classes/booking-dialog-boat';
import { resolveBoatLength } from '@shared/functions/resolve-boat-length';

@Component({
    selector: 'form-widget-boat',
    templateUrl: './form-widget-boat.component.html',
    standalone: false
})
export class FormWidgetBoatComponent implements OnInit, OnDestroy, OnChanges {

    private _unsubscribeAll = new Subject<any>();

    private searchTerm$ = new BehaviorSubject<string>('');

    private ownerFilter$ = new BehaviorSubject<{ personId?: number; companyId?: number } | null>(null);

    private ownerBoats$ = new BehaviorSubject<BoatOption[]>([]);

    private givenBoats$ = new BehaviorSubject<BoatOption[]>([]);

    private _preselectionApplied = false;

    @Input()
    readonly = false;

    @Input()
    appearance: MatFormFieldAppearance = 'outline';

    @Input()
    headline = TranslationService.translate('boats.selectOrCreateHeadline');

    @Input()
    givenBerth: Berth;

    // Client-side alternative to [person$]/[company$]: boats already known to the caller
    // (usually person.boats / company.boats) that belong on top of the dropdown.
    @Input()
    givenBoats: Boat[];

    @Input()
    showMetaData: boolean;

    @Input()
    boat$: Observable<Boat>;

    @Input()
    set selectBoat(value: Boat) {
        if (value?.id || value?.id !== this._selectBoat?.id) {
            this._selectBoat = value;
        }
    }

    get selectBoat(): Boat {
        return this._selectBoat;
    }

    private _selectBoat: Boat;

    @Input()
    boatRequired: boolean;

    @Input()
    optionalFields: boolean;

    @Input()
    canCreate = true;

    @Input()
    slimmedView = false;

    @Input()
    boatOwnerName: string;

    @Input()
    canSave: boolean;

    @Input()
    boatId: number;

    @Input()
    predefinedValues: PredefinedBoatValues;

    @Input()
    editable = false;

    @Input()
    person$: Observable<Person>;

    @Input()
    company$: Observable<Company>;

    @Input()
    general$: Observable<BcmBookingGeneralData>;

    @Output()
    boatChanged = new Subject<Boat>();

    personOrCompany: 'person' | 'company';

    person: Person;

    company: Company;

    boats: BoatOption[] = [];

    displayBoatOwnerName: string;

    isSaving: boolean;

    optionalString: string;

    boatFormGroup: UntypedFormGroup;

    boat: Boat;

    loadingBoats: boolean;

    loadingSelectedBoat = false;

    isSearching = false;

    hasInitiallyLoaded = false;

    topList: BoatOption[] = [];

    bottomList: BoatOption[] = [];

    filteredTopList$: BehaviorSubject<BoatOption[]> = new BehaviorSubject<BoatOption[]>([]);

    filteredBottomList$: BehaviorSubject<BoatOption[]> = new BehaviorSubject<BoatOption[]>([]);

    private resolvingUp2boatId = false;

    constructor(private _formBuilder: UntypedFormBuilder,
                private _boatApiService: BoatApiService,
                private formGroupDirective: FormGroupDirective,
                private _dialog: MatDialog,
                private up2boatIdSelection: Up2boatIdSelectionService) {
    }

    private async applyUp2boatBoat(code: string): Promise<void> {
        if (this.resolvingUp2boatId) {
            return;
        }

        this.resolvingUp2boatId = true;

        try {
            const resolution = await this.up2boatIdSelection.resolveBoat(code);

            if (!resolution.found) {
                // Keine bekannte Up2Boat-ID (z. B. Bootsname): normale Suche.
                this.searchTerm$.next(code);
                return;
            }

            if (resolution.value) {
                this.boatFormGroup.get('boat').setValue(resolution.value);
            }
        } finally {
            this.resolvingUp2boatId = false;
        }
    }

    ngOnInit(): void {

        this.boat = this.selectBoat;

        if (!this.canCreate) {
            this.headline = TranslationService.translate('selectBoat');
        }

        this._createForm();
        this.givenBoats$.next(this._toBoatOptions(this.givenBoats));
        this._loadBoats();

        if (this.formGroupDirective?.form) {
            this.formGroupDirective.form.addControl('boatForm', this.boatFormGroup);
        }

        if (isFunction(this.boat$?.subscribe)) {
            this.boat$
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(boat => {
                    this.boatFormGroup.patchValue({boat, ...boat}, {emitEvent: false});

                    this.boatFormGroup.markAsDirty();
                });
        }

        if (isFunction(this.general$?.subscribe)) {
            this.general$
                .pipe(
                    takeUntil(this._unsubscribeAll),
                    distinctUntilChanged((previous, current) => previous?.personOrCompany === current?.personOrCompany),
                    debounceTime(DEFAULT_DEBOUNCE_TIME)
                )
                .subscribe(general => {
                    this.personOrCompany = general?.personOrCompany;
                });
        }

        if (isFunction(this.person$?.subscribe)) {
            this.person$
                .pipe(
                    takeUntil(this._unsubscribeAll),
                    distinctUntilChanged((previous, current) => previous?.id === current?.id),
                    debounceTime(DEFAULT_DEBOUNCE_TIME)
                )
                .subscribe(person => {

                    if (person) {
                        this.company = null;
                    }

                    this.person = person;
                    this.onPersonOrCompanyChange();
                });
        }

        if (isFunction(this.company$?.subscribe)) {
            this.company$
                .pipe(
                    takeUntil(this._unsubscribeAll),
                    distinctUntilChanged((previous, current) => previous?.id === current?.id),
                    debounceTime(DEFAULT_DEBOUNCE_TIME)
                )
                .subscribe(company => {
                    if (company) {
                        this.person = null;
                    }
                    this.company = company;

                    this.onPersonOrCompanyChange();
                });
        }

    }

    onPersonOrCompanyChange(): void {

        // Fall back to inferring from person/company presence when no general$ input
        // sets the explicit personOrCompany flag.
        const usePerson = this.personOrCompany === 'person'
            || (!this.personOrCompany && !!this.person);
        const useCompany = this.personOrCompany === 'company'
            || (!this.personOrCompany && !!this.company);

        if (usePerson && this.person) {
            this.displayBoatOwnerName = this.person.fullName;
            this.boatOwnerName = this.person.fullName;
            this.ownerFilter$.next({ personId: this.person.id });
        } else if (useCompany && this.company) {
            this.displayBoatOwnerName = this.company.name;
            this.boatOwnerName = this.company.name;
            this.ownerFilter$.next({ companyId: this.company.id });
        } else {
            this.displayBoatOwnerName = null;
            this.boatOwnerName = null;
            this.ownerFilter$.next(null);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {

        // Ahead of the form guard — givenBoats feeds the top list, which does not need the form.
        if (changes.givenBoats) {
            this.givenBoats$.next(this._toBoatOptions(this.givenBoats));
        }

        if (!this.boatFormGroup) {
            return;
        }

        // Only sync when a real Boat (with id) arrives. Transient string values from
        // parent [selectBoat] bindings that mirror the typed search text would otherwise
        // loop back here, fire mat-autocomplete writeValue, and displayBoatWith() would
        // format the string to '' — wiping the user's keystrokes.
        if (this.selectBoat?.id) {
            this.boatFormGroup.patchValue({boat: this.selectBoat}, {emitEvent: false});
        }
    }

    ngOnDestroy(): void {
        if (this.formGroupDirective?.form) {
            this.formGroupDirective.form.removeControl('boatForm');
        }
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    onClickRemoveBoat(): void {
        this.boat = null;
        this.boatFormGroup.get('boat').reset('', {emitEvent: false});
        this.boatFormGroup.markAsDirty();
        this.boatChanged.next(null);
        this.searchTerm$.next('');
    }

    openEditDialog(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this._dialog.open(BoatEditorDialogComponent, {
            data: {
                parentFormGroup: this.formGroupDirective.form,
                boatFormGroup: this.boatFormGroup,
                boat: this.boat,
                optionalFields: this.optionalFields,
                appearance: this.appearance,
                predefinedValues: this.predefinedValues,
            },
            disableClose: true,
        }).afterClosed().subscribe((boat: Boat) => {
            if (!boat) {
                return;
            }

            if (this.boat?.id === boat.id) {
                this.boatFormGroup.markAsPristine();
                this.boatFormGroup.markAsUntouched();
            }

            this.boat = boat;
            this.boatFormGroup.get('boat').setValue(boat);
        });
    }

    public displayBoatWith(boat: Boat | BoatOption): string {
        return boat ? [boat.licensePlate, boat.name, boat.manufacturer || boat.type].filter(_ => !!_).join(', ') : '';
    }

    private _createForm(): void {

        this.boatFormGroup = this._formBuilder.group(
            {
                boat: [this.selectBoat, this.boatRequired ? [U2bValidators.required(TranslationService.translate('boats.selectRequired'))] : []],
                name: [{value: null, disabled: !!this.boat}],
                length: [
                    {value: null, disabled: !!this.boat},
                    this.optionalFields ? [] : [
                        U2bValidators.required(TranslationService.translate('common.validationMsg.lengthRequired')),
                        U2bNumericValidators.numberFormat(),
                        U2bNumericValidators.numberMin(0.5),
                        U2bNumericValidators.numberMax(500)
                    ].filter(_ => _ != null)
                ],
                lengthOverall: [
                    {value: null, disabled: !!this.boat},
                    this.optionalFields ? [] : [
                        U2bValidators.required(TranslationService.translate('common.validationMsg.lengthRequired')),
                        U2bNumericValidators.numberFormat(),
                        U2bNumericValidators.numberMin(0.5),
                        U2bNumericValidators.numberMax(500)
                    ].filter(_ => _ != null)
                ],
                width: [
                    {value: null, disabled: !!this.boat},
                    this.optionalFields ? [] : [
                        U2bValidators.required(TranslationService.translate('common.validationMsg.widthRequired')),
                        U2bNumericValidators.numberFormat(),
                        U2bNumericValidators.numberMin(0.5),
                        U2bNumericValidators.numberMax(100)
                    ].filter(_ => _ != null)
                ],
                depth: [
                    {value: null, disabled: !!this.boat},
                    [
                        U2bNumericValidators.numberFormat(),
                        U2bNumericValidators.numberMin(0),
                        U2bNumericValidators.numberMax(20)
                    ]
                ],
                licensePlate: [{value: null, disabled: !!this.boat}],
                note: [
                    {value: null, disabled: !!this.boat},
                    [U2bValidators.maxLength(1024)]
                ],
            },
            {
                validators: [
                    form => {
                        if (this.optionalFields || !this.canCreate) {
                            return null;
                        }

                        if (!form || this.boat?.id) {
                            return null;
                        }

                        const nameControl: AbstractControl = form.get('name');
                        const licensePlateControl: AbstractControl = form.get('licensePlate');

                        if (nameControl.disabled || licensePlateControl.disabled) {
                            return null;
                        }

                        if (!nameControl.value && !licensePlateControl.value) {
                            const error: ValidationErrors = {
                                nameLicensePlate: {
                                    message: TranslationService.translate('boats.validation.identifyNameOrLicense')
                                }
                            };
                            nameControl.setErrors(error);
                            licensePlateControl.setErrors(error);
                            return error;
                        }

                        form.get('boat').setErrors(null);
                        nameControl.setErrors(null);
                        licensePlateControl.setErrors(null);
                        return null;
                    }
                ]
            }
        );

        if (!this.canCreate && !this.editable) {
            disableControlsByName(
                this.boatFormGroup,
                ['name', 'length', 'width', 'depth', 'note', 'licensePlate', 'lengthOverall']
            );
        }

        this.boatFormGroup
            .get('boat')
            .valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((boat) => {

                if (boat && boat.id) {
                    if (boat instanceof BoatOption) {
                        // User just picked a slim option from the dropdown — fetch the full Boat.
                        this._hydrateSelectedBoat(boat.id);
                        return;
                    }

                    this.boatChanged.next(boat);

                    this.boat = boat;

                    disableControlsByName(
                        this.boatFormGroup,
                        ['name', 'length', 'width', 'depth', 'note', 'licensePlate', 'lengthOverall']
                    );
                } else {
                    enableControlsByName(
                        this.boatFormGroup,
                        ['name', 'length', 'width', 'depth', 'note', 'licensePlate', 'lengthOverall'],
                        true
                    );
                }
            });

        this.formGroupDirective.form.valueChanges.subscribe(value => {

            // Explicit person$/company$ inputs are the source of truth — skip the
            // parent-form-discovery fallback so it doesn't clobber displayBoatOwnerName.
            if (this.person || this.company) {
                return;
            }

            const personForm = value?.personForm;
            const person = personForm?.person;
            const companyForm = value?.companyForm;
            const company = companyForm?.company;
            const previousOwnerName = this.displayBoatOwnerName;

            if (company || companyForm?.name) {
                this.displayBoatOwnerName = company?.toString() || companyForm?.name;
            } else if (person || (personForm?.firstName && personForm?.lastName)) {
                this.displayBoatOwnerName = person?.toString() || (personForm?.firstName + ' ' + personForm?.lastName).trim();
            } else {
                this.displayBoatOwnerName = null;
            }

            // _hasOwnerName decides whether givenBoats may go on top, so a name discovered here
            // has to re-run the split — this subscription alone never touches the lists.
            if (!previousOwnerName !== !this.displayBoatOwnerName) {
                this.givenBoats$.next(this.givenBoats$.value);
            }
        });
    }

    private _loadBoats(): void {
        this.loadingBoats = true;

        this.boatFormGroup.get('boat').valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter(value => isString(value)),
                debounceTime(DEFAULT_DEBOUNCE_TIME),
                distinctUntilChanged()
            )
            .subscribe(searchTerm => {
                if (isUp2boatId(searchTerm)) {
                    void this.applyUp2boatBoat(searchTerm);
                    return;
                }

                this.searchTerm$.next(searchTerm);
            });

        this.ownerFilter$
            .pipe(
                takeUntil(this._unsubscribeAll),
                distinctUntilChanged((a, b) =>
                    (a?.personId ?? null) === (b?.personId ?? null) &&
                    (a?.companyId ?? null) === (b?.companyId ?? null)
                ),
                switchMap(ownerFilter => {
                    if (!ownerFilter || (!ownerFilter.personId && !ownerFilter.companyId)) {
                        return of([] as BoatOption[]);
                    }
                    let params = new HttpParams()
                        .set('page', '0')
                        .set('size', '200')
                        .set('filter', '');
                    if (ownerFilter.personId) {
                        params = params.set('personId', String(ownerFilter.personId));
                    }
                    if (ownerFilter.companyId) {
                        params = params.set('companyId', String(ownerFilter.companyId));
                    }
                    return this._boatApiService.getOptions(params).pipe(
                        map(page => page.results),
                        catchError(err => {
                            console.error('[form-widget-boat] failed to load owner boats', err);
                            return of([] as BoatOption[]);
                        })
                    );
                })
            )
            .subscribe(boats => this.ownerBoats$.next(boats));

        const catalog$ = this.searchTerm$.pipe(
            tap(() => {
                if (!this.hasInitiallyLoaded) {
                    this.loadingBoats = true;
                } else {
                    this.isSearching = true;
                }
            }),
            switchMap(searchTerm => {
                const params = new HttpParams()
                    .set('page', '0')
                    .set('size', '50')
                    .set('filter', searchTerm || '');
                return this._boatApiService.getOptions(params).pipe(
                    map(page => ({results: page.results, searchTerm})),
                    catchError(err => {
                        console.error('[form-widget-boat] failed to load boat catalog', err);
                        return of({results: [] as BoatOption[], searchTerm});
                    })
                );
            }),
            tap(() => {
                this.loadingBoats = false;
                this.isSearching = false;
                this.hasInitiallyLoaded = true;
            })
        );

        combineLatest([catalog$, this.ownerBoats$, this.givenBoats$])
            .pipe(
                takeUntil(this._unsubscribeAll),
                withLatestFrom(this.boat$ || of(null))
            )
            .subscribe(([[{results, searchTerm}, ownerBoats, givenBoats], preSelectedBoat]) => {
                this._updateBoatLists(results, this._unionById(ownerBoats, givenBoats), searchTerm, preSelectedBoat);
            });
    }

    /**
     * Maps caller-provided boats onto BoatOption. Beyond normalising the shape this keeps the
     * selection path intact: only a BoatOption triggers _hydrateSelectedBoat(), so callers still
     * receive a complete Boat even though person.boats/company.boats arrive slim from the API.
     */
    private _toBoatOptions(boats: Boat[]): BoatOption[] {
        return (boats || [])
            .filter(boat => !!boat?.id)
            .map(boat => boat instanceof BoatOption ? boat : new BoatOption({
                id: boat.id,
                name: boat.name,
                licensePlate: boat.licensePlate,
                manufacturer: boat.manufacturer,
                type: boat.type,
                length: boat.length,
                lengthOverall: boat.lengthOverall,
                width: boat.width,
                depth: boat.depth,
                owner: boat.owner,
                ownerCompany: boat.ownerCompany,
            }));
    }

    /** LüA with hull-length fallback — same rule as booking search (#7227). */
    boatOptionLength(boat: BoatOption): number | null {
        return resolveBoatLength(boat);
    }

    showBoatOptionDimensions(boat: BoatOption): boolean {
        return this.boatOptionLength(boat) != null
            || boat?.width != null
            || boat?.depth != null;
    }

    // Mirrors the top group's *ngIf in the template — keep both in sync.
    private get _hasOwnerName(): boolean {
        return this.boatOwnerName?.length > 0 || this.displayBoatOwnerName?.length > 0;
    }

    // Server-sourced options come first — they carry the owner relation the slim caller payload lacks.
    private _unionById(primary: BoatOption[], secondary: BoatOption[]): BoatOption[] {
        const ids = new Set(primary.map(boat => boat.id));
        return [...primary, ...secondary.filter(boat => !ids.has(boat.id))];
    }

    private _updateBoatLists(catalog: BoatOption[], ownerBoats: BoatOption[], searchTerm: string, preSelectedBoat: Boat): void {
        const term = (searchTerm || '').trim().toLowerCase();
        const matchesTerm = (b: BoatOption): boolean => {
            if (!term) { return true; }
            return [b.name, b.licensePlate, b.manufacturer, b.type]
                .some(v => typeof v === 'string' && v.toLowerCase().includes(term));
        };

        // The top group only renders when an owner name is known. Without one, keeping boats in
        // topList would hide them outright — they are excluded from bottomList right below.
        this.topList = this._hasOwnerName ? ownerBoats.filter(matchesTerm) : [];
        const topIds = new Set(this.topList.map(b => b.id));
        this.bottomList = catalog.filter(b => !topIds.has(b.id));
        this.boats = [...this.topList, ...this.bottomList];

        this.filteredTopList$.next(this.topList);
        this.filteredBottomList$.next(this.bottomList);

        if (this._preselectionApplied || searchTerm) {
            return;
        }

        const preSelectedBoatId = this.boatId || this.selectBoat?.id || preSelectedBoat?.id;
        if (preSelectedBoatId) {
            this._preselectionApplied = true;
            this._applyPreselection(preSelectedBoatId, preSelectedBoat);
        }
    }

    private _applyPreselection(boatId: number, fallbackBoat: Boat): void {
        if (fallbackBoat?.id === boatId) {
            this._setSelectedBoat(fallbackBoat);
            return;
        }

        this._boatApiService.getOne(boatId)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(boat => this._setSelectedBoat(boat));
    }

    private _setSelectedBoat(boat: Boat): void {
        if (!boat) {
            return;
        }

        this.boat = boat;
        this.boatFormGroup.patchValue({boat}, {emitEvent: false});
    }

    private _hydrateSelectedBoat(boatId: number): void {
        this.loadingSelectedBoat = true;
        this._boatApiService.getOne(boatId)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(fullBoat => {
                this.loadingSelectedBoat = false;

                // Guard against a later selection overwriting a newer one if two hydrations race.
                const current = this.boatFormGroup.get('boat').value;
                if (current?.id !== fullBoat.id) {
                    return;
                }

                this.boat = fullBoat;
                this.boatFormGroup.get('boat').setValue(fullBoat, {emitEvent: false});
                this.boatChanged.next(fullBoat);
                disableControlsByName(
                    this.boatFormGroup,
                    ['name', 'length', 'width', 'depth', 'note', 'licensePlate', 'lengthOverall']
                );
            });
    }

}
